import React, { useState, useEffect } from "react";
import { getAllTicketService, raiseATicketService } from "../../services/ticketService";
import LogTicketForm from "./LogTicketForm";
import { Ticket } from "../../interface/ticket";
import ChatBox from "./ChatBox";
import TicketList from "./TicketList";

export default function TicketContent() {
  const [activeView, setActiveView] = useState<
    "logATicket" | "myTickets" | "openTickets" | "closedTickets"
  >("myTickets");

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null); // 👈 selected ticket
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTickets() {
      try {
        const response = await getAllTicketService();
        if (response.success) {
          setTickets(response.message.tickets);
        }
      } catch (err) {
        setError("Failed to fetch tickets.");
      } finally {
        setLoading(false);
      }
    }
    fetchTickets();
  }, []);

  const handleNewTicket = async (data: any) => {
    try {
      setLoading(true);
      const response = await raiseATicketService(data);
      if (response.success) {
        // reload tickets
        const refreshed = await getAllTicketService();
        if (refreshed.success) setTickets(refreshed.message.tickets);
        setSelectedTicket(null);
        setActiveView("openTickets");
      }
    } catch (err) {
      console.error("Error creating ticket:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex w-full max-w-7xl flex-col gap-6 md:flex-row">
      {/* Left Section */}
      <div className="flex flex-[4] flex-col rounded-lg border bg-white p-6 shadow-sm">
        {loading && <p className="text-gray-500">Loading tickets...</p>}
        {error && <p className="text-red-500">{error}</p>}

        {!loading && !error && (
          <>
            {activeView === "logATicket" ? (
              <LogTicketForm onSubmit={handleNewTicket} />
            ) : selectedTicket ? ( // 👈 if ticket selected → show chat
              <ChatBox ticket={selectedTicket} onClose={() => setSelectedTicket(null)} />
            ) : (
              <TicketList
                tickets={tickets}
                view={activeView}
                onSelectTicket={(ticket) => setSelectedTicket(ticket)} // 👈 pass handler
              />
            )}
          </>
        )}
      </div>

      {/* Sidebar */}
      <aside className="flex-[1] w-full md:w-56 rounded-lg border bg-white p-5 shadow-sm">
        <h3 className="mb-4 text-lg font-semibold text-gray-800">Views</h3>
        <ul className="space-y-3 text-gray-700">
          <li>
            <button
              onClick={() => {
                setSelectedTicket(null); // 👈 close chat when switching views
                setActiveView("logATicket");
              }}
              className={`${activeView === "logATicket"
                ? "text-blue-700 font-bold"
                : "hover:text-blue-800"
              }`}
            >
              Log a Ticket
            </button>
          </li>
          <li>
            <button
              onClick={() => {
                setSelectedTicket(null);
                setActiveView("myTickets");
              }}
              className={`${activeView === "myTickets"
                ? "text-blue-700 font-bold"
                : "hover:text-blue-800"
              }`}
            >
              My Tickets
            </button>
          </li>
          <li>
            <button
              onClick={() => {
                setSelectedTicket(null);
                setActiveView("openTickets");
              }}
              className={`${activeView === "openTickets"
                ? "text-blue-700 font-bold"
                : "hover:text-blue-800"
              }`}
            >
              My Open Tickets
            </button>
          </li>
          <li>
            <button
              onClick={() => {
                setSelectedTicket(null);
                setActiveView("closedTickets");
              }}
              className={`${activeView === "closedTickets"
                ? "text-blue-700 font-bold"
                : "hover:text-blue-800"
              }`}
            >
              My Closed Tickets
            </button>
          </li>
        </ul>
      </aside>
    </div>
  );
}
